import { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, CalendarDays, Check, X, Loader2, RefreshCw, Save, Globe2, Edit3, Trash2,
  PieChart as PieIcon,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

// Quota table split:
//   * `LIMITED_QUOTA_KEYS` — admin-set, editable annual cap (PL/CL).
//   * `USAGE_ONLY_KEYS`    — open types; we surface the used count
//                            for visibility but no annual is enforced.
const LIMITED_QUOTA_KEYS = ["PL", "CL"];
const USAGE_ONLY_KEYS = ["UPL", "AUPL", "HD", "AHD", "UHD"];
const QUOTA_LABEL = {
  PL: "Paid Leave", CL: "Casual Leave", UPL: "Unplanned",
  AUPL: "Approved Unplanned", HD: "Half Day",
  AHD: "Approved Half Day", UHD: "Unapproved Half Day",
};
// Order shown in the apply dropdown. AHD/UHD are at the bottom because
// they're typically admin-derived; we still expose them so users can
// log retroactive entries when needed.
const APPLY_TYPE_ORDER = ["PL", "CL", "UPL", "AUPL", "HD", "AHD", "UHD"];
const COUNTRY_LABEL = {
  US: "United States", IN: "India", UK: "United Kingdom",
  CA: "Canada", EU: "Europe", SG: "Singapore",
};
const COUNTRY_ORDER = ["US", "IN", "UK", "CA", "EU", "SG"];

const STATUS_TONE = {
  pending: "badge-info",
  approved: "badge-success",
  rejected: "badge-error",
  cancelled: "badge-neutral",
};

export default function Leaves() {
  const { user } = useAuth();
  // True base admin only. `user.role` is rewritten to the base role
  // (admin/agent/qa) by `get_current_user`, so HR/TL custom roles —
  // which have `base_role=admin` — would falsely match `role === "admin"`.
  // We check `role_label` (the user's stored role name) which stays
  // "admin" for true admins and "HR" / "Team Lead" / etc. for everyone
  // else, so HR/TL never see the Approvals / Quotas tabs.
  const isAdmin = (user?.role_label || user?.role) === "admin";
  const year = new Date().getFullYear();

  // Two page-permission gates fetched together:
  //   • view_leaves_report → shows the Report tab (read-only analytics).
  //   • admin_leaves       → shows the Approvals + Quotas tabs (write access
  //     to approve/reject leaves + set per-user annual quotas). Historically
  //     these were `isAdmin`-only; now admin can hand this to HR / HR Team
  //     Lead / any role via Users & Permissions → Admin Panel → tick "Leaves
  //     (Approvals + Quotas + Holidays)". Backend already enforces the
  //     same gate via `require_admin_subpage("admin_leaves")`.
  const [canViewReport, setCanViewReport] = useState(isAdmin);
  const [canManageLeaves, setCanManageLeaves] = useState(isAdmin);
  useEffect(() => {
    if (isAdmin) { setCanViewReport(true); setCanManageLeaves(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/permissions/effective");
        if (cancelled) return;
        const pp = data.page_permissions || {};
        setCanViewReport(!!pp.view_leaves_report);
        setCanManageLeaves(!!pp.admin_leaves);
      } catch (e) { /* keep defaults off */ void e; }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  return (
    <div className="page-shell">
      <PageHeader
        icon={<CalendarDays size={20} className="text-primary" />}
        title="Leaves"
        subtitle="Apply for time off · track your balance · view the public-holiday calendar"
      />
      <Tabs defaultValue="mine" className="px-4 pb-6">
        <TabsList className="rounded-sm">
          <TabsTrigger value="mine" data-testid="leaves-tab-mine">My Leaves</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="leaves-tab-holidays">Holiday Calendar</TabsTrigger>
          {canManageLeaves && (
            <>
              <TabsTrigger value="approvals" data-testid="leaves-tab-approvals">Approvals</TabsTrigger>
              <TabsTrigger value="quotas" data-testid="leaves-tab-quotas">Quotas</TabsTrigger>
            </>
          )}
          {canViewReport && (
            <TabsTrigger value="report" data-testid="leaves-tab-report">Report</TabsTrigger>
          )}
          <TabsTrigger value="shift-change" data-testid="leaves-tab-shift-change">Shift Change</TabsTrigger>
        </TabsList>

        <TabsContent value="mine"><MyLeavesPanel year={year} /></TabsContent>
        <TabsContent value="holidays"><HolidaysPanel isAdmin={canManageLeaves} /></TabsContent>
        {canManageLeaves && (
          <>
            <TabsContent value="approvals"><ApprovalsPanel /></TabsContent>
            <TabsContent value="quotas"><QuotasPanel year={year} /></TabsContent>
          </>
        )}
        {canViewReport && (
          <TabsContent value="report"><LeavesReportPanel year={year} /></TabsContent>
        )}
        <TabsContent value="shift-change"><ShiftChangePanel isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * My Leaves — balance cards + apply form + history table
 * ───────────────────────────────────────────────────────────────────────── */
function MyLeavesPanel({ year }) {
  const [bal, setBal] = useState(null);
  const [history, setHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: h }, { data: n }] = await Promise.all([
        api.get("/leaves/quota/me"),
        api.get("/leaves/mine"),
        api.get("/leaves/notifications/me"),
      ]);
      setBal(b);
      setHistory(h.items || []);
      setNotifications(n.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const dismissNotice = async (id) => {
    try {
      await api.post(`/leaves/notifications/${id}/dismiss`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to dismiss");
    }
  };

  // Auto-refetch balance whenever the user comes back to this tab. Solves the
  // race "admin updates my quota while my Leaves tab is open" — without this
  // the cards stay frozen at the value they had when the user originally
  // navigated to /leaves until they hit the refresh button.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  return (
    <div className="space-y-4 mt-3" data-testid="leaves-mine-panel">
      {notifications.length > 0 && (
        <div className="space-y-2" data-testid="leaves-notifications">
          {notifications.map((n) => (
            <div
              key={n.id}
              className="border border-amber-300 bg-amber-50 rounded-sm px-3 py-2.5 flex items-start gap-3"
              data-testid={`leaves-notification-${n.id}`}
            >
              <div className="text-amber-700 mt-0.5"><RefreshCw size={14} /></div>
              <div className="flex-1 text-xs">
                <div className="font-semibold text-amber-900">
                  Please re-apply for your leave
                </div>
                <div className="mt-1 text-amber-800">
                  An admin removed your <strong>{n.deleted_request?.leave_type}</strong> request
                  {n.deleted_request?.start_date && (
                    <> for {n.deleted_request.start_date}
                      {n.deleted_request.end_date && n.deleted_request.end_date !== n.deleted_request.start_date
                        ? ` → ${n.deleted_request.end_date}`
                        : ""}
                    </>
                  )}
                  {" "}({n.deleted_request?.days} day{n.deleted_request?.days === 1 ? "" : "s"})
                  {" "}because it was logged incorrectly. Please re-apply with the correct details.
                </div>
                {n.note && (
                  <div className="mt-1 italic text-amber-700">
                    Admin&apos;s note: &ldquo;{n.note}&rdquo;
                  </div>
                )}
                <div className="mt-1 text-[10px] text-amber-700">
                  — {n.created_by} · {(n.created_at || "").slice(0, 10)}
                </div>
              </div>
              <button
                onClick={() => dismissNotice(n.id)}
                className="text-amber-700 hover:text-amber-900 text-xs"
                data-testid={`leaves-notification-dismiss-${n.id}`}
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="leaves-balance-grid">
        {APPLY_TYPE_ORDER.map((k) => {
          const v = bal?.balances?.[k] || { annual: 0, consumed: 0, pending: 0, available: 0 };
          const limited = k === "PL" || k === "CL";
          return (
            <div key={k} className="border border-border rounded-sm p-3 bg-white" data-testid={`leaves-balance-${k}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</span>
                <span className={`text-[9px] uppercase tracking-wider font-semibold ${limited ? "text-blue-600" : "text-emerald-600"}`}>
                  {limited ? "Limited" : "Open"}
                </span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{QUOTA_LABEL[k]}</div>
              {limited ? (
                <>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold tabular-nums">{v.available}</span>
                    <span className="text-xs text-muted-foreground">/ {v.annual}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    used {v.consumed} · pending {v.pending}
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold tabular-nums">{v.consumed}</span>
                    <span className="text-xs text-muted-foreground">taken</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1">
                    {v.pending > 0 ? `pending ${v.pending} · ` : ""}no cap
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={() => setApplyOpen(true)} className="rounded-sm h-9" data-testid="leaves-apply-btn">
          <Plus size={14} className="mr-1.5" /> Apply for leave
        </Button>
        <Button onClick={refresh} variant="outline" className="rounded-sm h-9" data-testid="leaves-refresh-btn">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </Button>
        <span className="text-xs text-muted-foreground">{year} balances · Holiday (HL) is auto-granted on the public-holiday calendar.</span>
      </div>

      <HistoryTable items={history} testid="leaves-mine-history" showApprover onCancelled={refresh} />
      <ApplyLeaveDialog open={applyOpen} onClose={() => setApplyOpen(false)} onApplied={refresh} balances={bal?.balances} />
    </div>
  );
}

function HistoryTable({ items, testid, showApprover, onCancelled }) {
  // Pending requests can be cancelled by the user themselves; the
  // backend refuses cancel attempts on already-decided rows. We show
  // the small "Cancel" pill only for status=pending so the UI matches
  // what the API will accept.
  const cancel = async (r) => {
    if (!window.confirm(
      `Cancel this ${r.leave_type} request for ${r.start_date}` +
      `${r.end_date !== r.start_date ? ` → ${r.end_date}` : ""}?\n\n` +
      "If it was a PL or CL, the days will be refunded to your balance immediately."
    )) return;
    try {
      await api.post(`/leaves/${r.id}/cancel`);
      toast.success(`Cancelled · balance refunded if applicable`);
      onCancelled?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to cancel");
    }
  };

  return (
    <div className="border border-border rounded-sm overflow-auto" data-testid={testid}>
      <table className="min-w-full text-xs">
        <thead className="bg-slate-800 text-slate-100">
          <tr>
            <th className="px-3 py-2 text-left">Type</th>
            <th className="px-3 py-2 text-left">Start</th>
            <th className="px-3 py-2 text-left">End</th>
            <th className="px-3 py-2 text-right">Days</th>
            <th className="px-3 py-2 text-left">Reason</th>
            <th className="px-3 py-2 text-left">Status</th>
            {showApprover && <th className="px-3 py-2 text-left">Decision</th>}
            {onCancelled && <th className="px-3 py-2 text-right">Action</th>}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr><td colSpan={(showApprover ? 7 : 6) + (onCancelled ? 1 : 0)} className="px-3 py-6 text-center text-muted-foreground">No leave records yet.</td></tr>
          ) : items.map((r) => (
            <tr key={r.id} className="border-t border-border" data-testid={`leaves-row-${r.id}`}>
              <td className="px-3 py-2 font-semibold tabular-nums">{r.leave_type}</td>
              <td className="px-3 py-2">{r.start_date}</td>
              <td className="px-3 py-2">{r.end_date}</td>
              <td className="px-3 py-2 text-right tabular-nums">{r.days}</td>
              <td className="px-3 py-2 max-w-[260px] truncate" title={r.reason}>{r.reason || "—"}</td>
              <td className="px-3 py-2"><Badge className={STATUS_TONE[r.status]}>{r.status}</Badge></td>
              {showApprover && (
                <td className="px-3 py-2 text-[11px] text-muted-foreground">
                  {r.decided_by ? `${r.decided_by} · ${(r.decided_at || "").slice(0, 10)}` : "—"}
                </td>
              )}
              {onCancelled && (
                <td className="px-3 py-2 text-right">
                  {r.status === "pending" ? (
                    <Button
                      size="sm" variant="outline"
                      onClick={() => cancel(r)}
                      className="h-6 text-[10px] border-rose-300 text-rose-700 hover:bg-rose-50 rounded-sm px-2"
                      data-testid={`leaves-cancel-${r.id}`}
                    >
                      <X size={11} className="mr-0.5" /> Cancel
                    </Button>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApplyLeaveDialog({ open, onClose, onApplied, balances }) {
  const [type, setType] = useState("PL");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Only PL & CL are quota-limited. UPL/AUPL/HD/AHD/UHD are unlimited
  // ("open"). When PL or CL is exhausted, we disable the option in the
  // dropdown and steer the user to the open types.
  const plAvailable = Number(balances?.PL?.available ?? 0);
  const clAvailable = Number(balances?.CL?.available ?? 0);
  const plExhausted = plAvailable <= 0;
  const clExhausted = clAvailable <= 0;
  const limitedExhausted = plExhausted && clExhausted;
  const isLimited = (k) => k === "PL" || k === "CL";
  const isExhausted = (k) => (k === "PL" && plExhausted) || (k === "CL" && clExhausted);

  useEffect(() => {
    if (open) {
      // Default to a type the user can actually submit.
      const fallback = plExhausted ? (clExhausted ? "UPL" : "CL") : "PL";
      setType(fallback);
      setStart(""); setEnd(""); setReason("");
    }
  }, [open, plExhausted, clExhausted]);

  const submit = async () => {
    if (!start || !end) return toast.error("Pick start & end dates");
    if (isExhausted(type)) {
      return toast.error(`You don't have any ${type} balance left. Please choose UPL, AUPL, or UHD instead.`);
    }
    setBusy(true);
    try {
      await api.post("/leaves/apply", { leave_type: type, start_date: start, end_date: end, reason });
      toast.success("Leave application submitted — waiting for admin approval.");
      onClose();
      onApplied?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="leaves-apply-dialog">
        <DialogHeader><DialogTitle>Apply for leave</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <label className="block">
            <span className="text-xs meta-label">Leave type</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full mt-1 h-9 rounded-sm border border-border px-2 text-sm"
              data-testid="leaves-apply-type"
            >
              {APPLY_TYPE_ORDER.map((k) => {
                const limited = isLimited(k);
                const exhausted = isExhausted(k);
                const avail = k === "PL" ? plAvailable : k === "CL" ? clAvailable : null;
                const labelSuffix = limited
                  ? ` (${avail} avail.)`
                  : " (open)";
                return (
                  <option key={k} value={k} disabled={exhausted}>
                    {k} · {QUOTA_LABEL[k]}{labelSuffix}{exhausted ? " — exhausted" : ""}
                  </option>
                );
              })}
            </select>
          </label>
          {(plExhausted || clExhausted) && (
            <p
              className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5"
              data-testid="leaves-quota-exhausted-warning"
            >
              {limitedExhausted ? (
                <>You have no <strong>PL</strong> or <strong>CL</strong> balance left.</>
              ) : plExhausted ? (
                <>Your <strong>PL</strong> balance is 0.</>
              ) : (
                <>Your <strong>CL</strong> balance is 0.</>
              )}
              {" "}Pick <strong>UPL</strong> (Unplanned), <strong>AUPL</strong> (Approved Unplanned), or <strong>UHD</strong> (Unapproved Half Day) — those are unlimited. PL accrues +1 each calendar month automatically.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs meta-label">Start date</span>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="leaves-apply-start" />
            </label>
            <label className="block">
              <span className="text-xs meta-label">End date</span>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="leaves-apply-end" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs meta-label">Reason (optional)</span>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., family wedding · cousin's reception" className="rounded-sm h-9 mt-1" data-testid="leaves-apply-reason" />
          </label>
          {type === "HD" && (
            <p className="text-[11px] text-muted-foreground">
              Half-day always counts as 0.5 regardless of dates. If admin <strong>approves</strong>, it becomes <strong>AHD</strong>; if <strong>rejected</strong>, it&apos;s logged as <strong>UHD</strong>.
            </p>
          )}
          {type === "PL" && !plExhausted && (
            <p className="text-[11px] text-muted-foreground">
              If admin rejects this PL request, it&apos;ll be re-classified as <strong>UPL</strong> (Unplanned) instead.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={busy || isExhausted(type)} className="rounded-sm" data-testid="leaves-apply-submit">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Approvals — admin only
 * ───────────────────────────────────────────────────────────────────────── */
function ApprovalsPanel() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leaves/requests?status=${statusFilter}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const decide = async (rid, kind) => {
    const reason = kind === "reject" ? (window.prompt("Reason for rejection (optional):") || "") : "";
    try {
      await api.post(`/leaves/${rid}/${kind}`, { reason });
      toast.success(`${kind === "approve" ? "Approved" : "Rejected"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  // Delete an incorrectly-applied request. Asks the admin whether to
  // notify the agent so they can re-apply with the right details.
  const removeRequest = async (r) => {
    if (!window.confirm(
      `Delete this leave request?\n\n${r.user_name} · ${r.leave_type} · ${r.start_date}` +
      `${r.start_date !== r.end_date ? ` → ${r.end_date}` : ""} (${r.days} day${r.days === 1 ? "" : "s"})\n\n` +
      "This permanently removes the entry. Quota usage will refresh accordingly."
    )) return;
    const notify = window.confirm(
      `Notify ${r.user_name} to re-apply?\n\n` +
      "Click OK to send a banner-style notification on their My Leaves tab.\n" +
      "Click Cancel to delete silently."
    );
    let note = "";
    if (notify) {
      note = window.prompt("Note for the agent (optional — what should they fix?):", "Please re-apply with the correct dates / type.") || "";
    }
    try {
      await api.delete(`/leaves/${r.id}`, { data: { notify, note } });
      toast.success(notify ? `Deleted · agent will see a re-apply banner` : "Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-3 mt-3" data-testid="leaves-approvals-panel">
      <div className="flex items-center gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`leaves-approvals-filter-${s}`}
            className={`h-8 px-3 rounded-sm text-xs border ${statusFilter === s ? "bg-primary text-white border-primary" : "bg-background border-border hover:bg-secondary/50"}`}>
            {s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
        <Button onClick={load} variant="outline" className="rounded-sm h-8 ml-auto">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>
      <div className="border border-border rounded-sm overflow-auto" data-testid="leaves-approvals-table">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-800 text-slate-100">
            <tr>
              <th className="px-3 py-2 text-left">Employee</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Start</th>
              <th className="px-3 py-2 text-left">End</th>
              <th className="px-3 py-2 text-right">Days</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Nothing here.</td></tr>
            ) : items.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`leaves-approval-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold">{r.user_name}</div>
                  <div className="text-[10px] text-muted-foreground">{r.user_email}</div>
                </td>
                <td className="px-3 py-2 font-semibold tabular-nums">{r.leave_type}</td>
                <td className="px-3 py-2">{r.start_date}</td>
                <td className="px-3 py-2">{r.end_date}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.days}</td>
                <td className="px-3 py-2 max-w-[200px] truncate" title={r.reason}>{r.reason || "—"}</td>
                <td className="px-3 py-2"><Badge className={STATUS_TONE[r.status]}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.status === "pending" ? (
                    <>
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "approve")}
                        className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm mr-1"
                        data-testid={`leaves-approve-${r.id}`}>
                        <Check size={12} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "reject")}
                        className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50 rounded-sm"
                        data-testid={`leaves-reject-${r.id}`}>
                        <X size={12} className="mr-1" /> Reject
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => removeRequest(r)}
                        className="h-7 text-[11px] border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm"
                        title="Delete this request (incorrectly applied)"
                        data-testid={`leaves-delete-${r.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[10px] text-muted-foreground">
                        {r.decided_by} · {(r.decided_at || "").slice(0, 10)}
                      </span>
                      <Button size="sm" variant="outline" onClick={() => removeRequest(r)}
                        className="h-7 text-[11px] border-slate-300 text-slate-600 hover:bg-slate-50 rounded-sm"
                        title="Delete this request (incorrectly applied)"
                        data-testid={`leaves-delete-${r.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Quotas — admin only
 * ───────────────────────────────────────────────────────────────────────── */
function QuotasPanel({ year }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({}); // {user_id: {PL,CL,...}}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leaves/quotas?year=${year}`);
      setRows(data.items || []);
      setEdits({});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const setVal = (uid, key, val) => {
    setEdits((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), [key]: Number(val) } }));
  };

  const save = async (row) => {
    const edit = edits[row.user_id] || {};
    // Send all 5 values the backend QuotaBody expects, but only PL/CL
    // are editable in the UI now. The open types (UPL/AUPL/HD) are kept
    // at 0 — they're unlimited at apply-time so the stored value is
    // unused. Admins shouldn't have to think about a number that has
    // no effect.
    const next = {
      PL:   edit.PL   ?? Math.round(row.balances.PL.annual),
      CL:   edit.CL   ?? Math.round(row.balances.CL.annual),
      UPL:  0,
      AUPL: 0,
      HD:   0,
    };
    try {
      await api.put(`/leaves/quota/${row.user_id}`, { year, ...next });
      toast.success(`Quota saved for ${row.user_name}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-3 mt-3" data-testid="leaves-quotas-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Assign annual <strong>PL</strong> and <strong>CL</strong> quotas per user for {year}. Other types
          (<span className="font-mono">UPL · AUPL · HD · AHD · UHD</span>) are <strong>unlimited</strong> — only the used count is shown for visibility.
          Edit cells then click <strong>Save</strong> on the row.
        </p>
        <Button onClick={load} variant="outline" className="rounded-sm h-8">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </Button>
      </div>
      <div className="border border-border rounded-sm overflow-auto" data-testid="leaves-quotas-table">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="bg-slate-800 text-slate-100">
              <th rowSpan={2} className="px-3 py-2 text-left align-middle">Employee</th>
              <th rowSpan={2} className="px-3 py-2 text-left align-middle">Role</th>
              {LIMITED_QUOTA_KEYS.map((k) => (
                <th key={k} colSpan={2} className="px-3 py-1 text-center border-l border-slate-600">
                  {k} · {QUOTA_LABEL[k]}
                </th>
              ))}
              <th colSpan={USAGE_ONLY_KEYS.length} className="px-3 py-1 text-center border-l border-slate-600">
                Used (open · no quota)
              </th>
              <th rowSpan={2} className="px-3 py-2 text-right align-middle">Action</th>
            </tr>
            <tr className="bg-slate-700 text-slate-200 text-[10px] uppercase tracking-wider">
              {LIMITED_QUOTA_KEYS.map((k) => (
                <Fragment key={k}>
                  <th className="px-2 py-1 text-right border-l border-slate-600">Quota</th>
                  <th className="px-2 py-1 text-right">Used</th>
                </Fragment>
              ))}
              {USAGE_ONLY_KEYS.map((k) => (
                <th key={k} className="px-2 py-1 text-right border-l border-slate-600">{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={LIMITED_QUOTA_KEYS.length * 2 + USAGE_ONLY_KEYS.length + 3} className="px-3 py-6 text-center text-muted-foreground">No users.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.user_id} className="border-t border-border" data-testid={`leaves-quota-row-${row.user_id}`}>
                <td className="px-3 py-2">
                  <div className="font-semibold">{row.user_name}</div>
                  <div className="text-[10px] text-muted-foreground">{row.user_email}</div>
                </td>
                <td className="px-3 py-2 capitalize">{row.role}</td>
                {LIMITED_QUOTA_KEYS.map((k) => {
                  const annual = Math.round(row.balances[k].annual);
                  const editVal = edits[row.user_id]?.[k];
                  const current = editVal == null ? annual : editVal;
                  const consumed = row.balances[k].consumed;
                  const isEdited = editVal != null && editVal !== annual;
                  return (
                    <Fragment key={k}>
                      <td className="px-2 py-2 text-right border-l border-border bg-slate-50/50">
                        <Input
                          type="number" min="0" max="365"
                          value={current}
                          onChange={(e) => setVal(row.user_id, k, e.target.value)}
                          className={`rounded-sm h-7 text-[11px] text-right w-16 ml-auto ${isEdited ? "border-amber-400 ring-1 ring-amber-200" : ""}`}
                          data-testid={`leaves-quota-input-${row.user_id}-${k}`}
                        />
                        {isEdited && (
                          <div className="text-[9px] text-amber-600 font-semibold mt-0.5">unsaved</div>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-muted-foreground" data-testid={`leaves-quota-used-${row.user_id}-${k}`}>
                        {consumed}
                      </td>
                    </Fragment>
                  );
                })}
                {USAGE_ONLY_KEYS.map((k) => {
                  const consumed = row.balances[k]?.consumed ?? 0;
                  return (
                    <td
                      key={k}
                      className="px-2 py-2 text-right tabular-nums border-l border-border text-muted-foreground"
                      title={`${QUOTA_LABEL[k]} — unlimited; ${consumed} taken this year`}
                      data-testid={`leaves-quota-used-${row.user_id}-${k}`}
                    >
                      {consumed || "—"}
                    </td>
                  );
                })}
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => save(row)}
                    className="rounded-sm h-7 text-[11px]" data-testid={`leaves-quota-save-${row.user_id}`}>
                    <Save size={11} className="mr-1" /> Save
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Holidays — read for everyone, write for admin
 * ───────────────────────────────────────────────────────────────────────── */
function HolidaysPanel({ isAdmin }) {
  const [country, setCountry] = useState("US");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showPending, setShowPending] = useState(false); // admin-only toggle
  const yearNow = useMemo(() => new Date().getFullYear(), []);

  const load = async () => {
    setLoading(true);
    try {
      // Admin can opt-in to seeing PENDING holidays alongside approved by
      // toggling `showPending`. Non-admins ALWAYS receive approved-only
      // (the backend enforces this regardless of the flag).
      const url = `/leaves/holidays?country=${country}&year=${yearNow}&active_only=false${isAdmin && showPending ? "&include_pending=true" : ""}`;
      const { data } = await api.get(url);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [country, showPending]); // eslint-disable-line

  const seed = async () => {
    try {
      const { data } = await api.post("/leaves/holidays/seed", {});
      toast.success(`Seeded ${data.added} default holiday${data.added === 1 ? "" : "s"} across all 6 regions (pending admin approval)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const approveOne = async (h) => {
    try {
      await api.post(`/leaves/holidays/${h.id}/approve`, {});
      toast.success(`Approved: ${h.name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const rejectOne = async (h) => {
    try {
      await api.post(`/leaves/holidays/${h.id}/reject`, {});
      toast.success(`Marked pending: ${h.name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const approveAll = async () => {
    if (!window.confirm(`Approve EVERY pending holiday in ${COUNTRY_LABEL[country]}?`)) return;
    try {
      const { data } = await api.post(`/leaves/holidays/approve-all-pending?country=${country}`, {});
      toast.success(`Approved ${data.modified} holidays`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}" (${h.holiday_date})?`)) return;
    try {
      await api.delete(`/leaves/holidays/${h.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const pendingCount = items.filter((h) => !h.is_approved).length;

  return (
    <div className="space-y-3 mt-3" data-testid="leaves-holidays-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Globe2 size={16} className="text-muted-foreground" />
        {COUNTRY_ORDER.map((c) => (
          <button key={c} onClick={() => setCountry(c)}
            data-testid={`leaves-country-${c}`}
            className={`h-8 px-3 rounded-sm text-xs border ${country === c ? "bg-primary text-white border-primary" : "bg-background border-border hover:bg-secondary/50"}`}>
            {c} · {COUNTRY_LABEL[c]}
          </button>
        ))}
        <Button onClick={load} variant="outline" className="rounded-sm h-8 ml-auto">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
        </Button>
        {isAdmin && (
          <>
            <label className="inline-flex items-center gap-1.5 text-xs ml-2" data-testid="leaves-show-pending-toggle">
              <input type="checkbox" checked={showPending} onChange={(e) => setShowPending(e.target.checked)} />
              Show pending {pendingCount > 0 && showPending ? `(${pendingCount})` : ""}
            </label>
            <Button onClick={() => setEditing({ country, name: "", holiday_date: "", type: "", details: "", is_active: true })}
              variant="outline" className="rounded-sm h-8" data-testid="leaves-holiday-add-btn">
              <Plus size={12} className="mr-1" /> Add
            </Button>
            <Button onClick={seed} variant="outline" className="rounded-sm h-8" data-testid="leaves-holiday-seed-btn">
              Seed 2026 defaults
            </Button>
            {showPending && pendingCount > 0 && (
              <Button onClick={approveAll} variant="outline"
                className="rounded-sm h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="leaves-holiday-approve-all-btn">
                <Check size={12} className="mr-1" /> Approve all pending ({pendingCount})
              </Button>
            )}
          </>
        )}
      </div>

      {!isAdmin && (
        <p className="text-[11px] text-muted-foreground" data-testid="leaves-holidays-readonly-note">
          View-only · You see holidays admin has approved. Need a date added? Ping the admin.
        </p>
      )}

      <div className="border border-border rounded-sm overflow-auto" data-testid="leaves-holidays-table">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-800 text-slate-100">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Weekday</th>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Details</th>
              <th className="px-3 py-2 text-left">Status</th>
              {isAdmin && <th className="px-3 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={isAdmin ? 7 : 6} className="px-3 py-6 text-center text-muted-foreground">No holidays for {COUNTRY_LABEL[country]} yet. {isAdmin && "Click 'Seed 2026 defaults' to bootstrap."}</td></tr>
            ) : items.map((h) => {
              const wd = new Date(h.holiday_date + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "long", timeZone: "UTC" });
              const pending = !h.is_approved;
              return (
                <tr key={h.id} className={`border-t border-border ${pending ? "bg-amber-50/40" : ""}`} data-testid={`leaves-holiday-row-${h.id}`}>
                  <td className="px-3 py-2 font-mono">{h.holiday_date}</td>
                  <td className="px-3 py-2">{wd}</td>
                  <td className="px-3 py-2 font-semibold">{h.name}</td>
                  <td className="px-3 py-2">{h.type || "—"}</td>
                  <td className="px-3 py-2 max-w-[320px] text-muted-foreground" title={h.details}>{h.details || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {pending
                      ? <Badge className="badge-warning">Pending</Badge>
                      : <Badge className="badge-success">Approved</Badge>}
                    {!h.is_active && <Badge className="badge-neutral ml-1">Inactive</Badge>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {pending ? (
                        <Button size="sm" variant="outline" onClick={() => approveOne(h)}
                          className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm mr-1"
                          data-testid={`leaves-holiday-approve-${h.id}`}>
                          <Check size={12} className="mr-1" /> Approve
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => rejectOne(h)}
                          className="h-7 text-[11px] text-amber-700 hover:bg-amber-50 rounded-sm mr-1"
                          data-testid={`leaves-holiday-unapprove-${h.id}`}>
                          Un-approve
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setEditing(h)} className="h-7 w-7 p-0">
                        <Edit3 size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(h)} className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50">
                        <Trash2 size={12} />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {isAdmin && editing && (
        <HolidayDialog
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function HolidayDialog({ row, onClose, onSaved }) {
  const [name, setName] = useState(row.name || "");
  const [date, setDate] = useState(row.holiday_date || "");
  const [country, setCountry] = useState(row.country || "US");
  const [type, setType] = useState(row.type || "");
  const [details, setDetails] = useState(row.details || "");
  const [active, setActive] = useState(row.is_active ?? true);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim() || !date) return toast.error("Name + date required");
    setBusy(true);
    try {
      const body = { country, name, holiday_date: date, type, details, is_active: active };
      if (row.id) await api.put(`/leaves/holidays/${row.id}`, body);
      else await api.post("/leaves/holidays", body);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="leaves-holiday-dialog">
        <DialogHeader><DialogTitle>{row.id ? "Edit" : "Add"} holiday</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <label className="block">
            <span className="text-xs meta-label">Country</span>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full mt-1 h-9 rounded-sm border border-border px-2 text-sm">
              {COUNTRY_ORDER.map((c) => (<option key={c} value={c}>{c} · {COUNTRY_LABEL[c]}</option>))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs meta-label">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="leaves-holiday-name" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-xs meta-label">Date</span>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm h-9 mt-1" data-testid="leaves-holiday-date" />
            </label>
            <label className="block">
              <span className="text-xs meta-label">Type</span>
              <Input value={type} onChange={(e) => setType(e.target.value)} placeholder="e.g., Federal Holiday" className="rounded-sm h-9 mt-1" data-testid="leaves-holiday-type" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs meta-label">Details</span>
            <Input value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Free-form notes" className="rounded-sm h-9 mt-1" data-testid="leaves-holiday-details" />
          </label>
          <label className="inline-flex items-center gap-2 text-xs">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm" data-testid="leaves-holiday-save">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─────────────────────────────────────────────────────────────────────────
 * Leaves Report — donut by leave-type + per-agent breakdown table
 *   Visible to admin OR any role with `view_leaves_report=true`.
 *   Mirrors the look of Admin → Reports → "Leads by Campaign" so the
 *   visual language is consistent across the app.
 * ───────────────────────────────────────────────────────────────────────── */
const TYPE_PALETTE = {
  PL:   "#2563EB",   // blue
  CL:   "#10B981",   // emerald
  UPL:  "#F59E0B",   // amber
  AUPL: "#84CC16",   // lime
  HD:   "#8B5CF6",   // violet
  AHD:  "#0EA5E9",   // sky
  UHD:  "#EF4444",   // red
  HL:   "#6B7280",   // gray
};
function colorForType(t, idx) {
  return TYPE_PALETTE[t] || ["#6366F1","#EC4899","#14B8A6","#F97316","#A3E635"][idx % 5];
}

function LeavesReportPanel({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Filters: month is 1-12 ("" = whole year). agentId "" = all agents.
  const [month, setMonth] = useState("");
  const [agentId, setAgentId] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (month) params.set("month", month);
      if (agentId) params.set("agent_id", agentId);
      const { data } = await api.get(`/leaves/report?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load report");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [year, month, agentId]);

  const chartData = useMemo(
    () => (data?.by_type || []).map((r) => ({ name: `${r.type} · ${r.label}`, value: r.count, type: r.type })),
    [data],
  );
  const totalReqs = data?.total_requests || 0;
  const monthOptions = [
    { v: "", label: "All months" },
    { v: "1", label: "January" }, { v: "2", label: "February" }, { v: "3", label: "March" },
    { v: "4", label: "April" }, { v: "5", label: "May" }, { v: "6", label: "June" },
    { v: "7", label: "July" }, { v: "8", label: "August" }, { v: "9", label: "September" },
    { v: "10", label: "October" }, { v: "11", label: "November" }, { v: "12", label: "December" },
  ];
  const periodLabel = month
    ? `${monthOptions.find((m) => m.v === month)?.label} ${data?.year || year}`
    : `${data?.year || year}`;

  return (
    <div className="space-y-4 mt-3" data-testid="leaves-report-panel">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <h2 className="text-sm font-semibold tracking-tight">Leaves Report · {periodLabel}</h2>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {totalReqs.toLocaleString()} request{totalReqs === 1 ? "" : "s"} {agentId ? "for selected agent" : "across all agents"}
          </p>
        </div>
        <label className="block">
          <span className="text-[10px] meta-label">Month</span>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="block mt-0.5 h-8 rounded-sm border border-border px-2 text-xs min-w-[140px]"
            data-testid="leaves-report-month"
          >
            {monthOptions.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] meta-label">Agent</span>
          <select
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            className="block mt-0.5 h-8 rounded-sm border border-border px-2 text-xs min-w-[200px]"
            data-testid="leaves-report-agent"
          >
            <option value="">All agents</option>
            {(data?.agents || []).map((a) => (
              <option key={a.id} value={a.id}>{a.name}{a.email ? ` · ${a.email}` : ""}</option>
            ))}
          </select>
        </label>
        <Button onClick={refresh} disabled={loading} variant="outline" size="sm" className="rounded-sm h-8" data-testid="leaves-report-refresh">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
        </Button>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-hidden" data-testid="leaves-report-card-by-type">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/40">
          <PieIcon size={14} className="text-primary" />
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Leaves By Type</h3>
          <span className="ml-auto text-sm tabular-nums" data-testid="leaves-report-total">{totalReqs.toLocaleString()}</span>
        </div>
        {chartData.length === 0 ? (
          <div className="text-xs text-muted-foreground italic py-16 text-center">No leave activity this year yet.</div>
        ) : (
          <>
            <div className="h-64 px-2 pt-3 pb-2">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 4, right: 4, bottom: 30, left: 4 }}>
                  <Pie data={chartData} innerRadius="55%" outerRadius="80%" paddingAngle={1} dataKey="value" nameKey="name" isAnimationActive={false} label={false} labelLine={false}>
                    {chartData.map((row, i) => (
                      <Cell key={row.type} fill={colorForType(row.type, i)} stroke="#fff" />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, padding: "6px 10px" }} formatter={(v, n) => [v.toLocaleString(), n]} />
                  <Legend verticalAlign="bottom" iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="border-t border-border overflow-y-auto max-h-56" data-testid="leaves-report-table-by-type">
              <table className="w-full text-xs">
                <thead className="bg-secondary sticky top-0 z-10 shadow-[0_1px_0_var(--border)]">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Leave Type</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Count</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">%</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.by_type || []).map((row, i) => (
                    <tr key={row.type} className="border-t border-border first:border-0" data-testid={`leaves-report-type-row-${row.type}`}>
                      <td className="px-3 py-1.5 flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colorForType(row.type, i) }} />
                        <span className="font-mono">{row.type}</span>
                        <span className="text-muted-foreground">· {row.label}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{row.count.toLocaleString()}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums text-muted-foreground">{row.pct.toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Monthly trend (count per month). Always full year so users see
          the seasonal pattern even when zoomed to a single month. The
          currently-selected month gets a stronger fill. */}
      <div className="rounded-sm border border-border bg-card overflow-hidden" data-testid="leaves-report-card-by-month">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/40">
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
            Monthly Trend · {data?.year || year}{agentId ? " (selected agent)" : ""}
          </h3>
        </div>
        {(data?.by_month || []).every((b) => b.count === 0) ? (
          <div className="text-xs text-muted-foreground italic py-12 text-center">No leave activity in {data?.year || year} yet.</div>
        ) : (
          <div className="h-56 px-2 pt-3 pb-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.by_month || []} margin={{ top: 4, right: 16, bottom: 16, left: 0 }} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} stroke="#94A3B8" />
                <Tooltip
                  contentStyle={{ fontSize: 11, padding: "6px 10px" }}
                  formatter={(v) => [v, "Requests"]}
                  labelFormatter={(lbl) => `${lbl} ${data?.year || year}`}
                />
                <Bar dataKey="count" name="count" radius={[3, 3, 0, 0]}>
                  {(data?.by_month || []).map((b) => (
                    <Cell
                      key={b.m}
                      fill={month && String(b.m) === month ? "#1D4ED8" : "#60A5FA"}
                      data-testid={`leaves-report-month-bar-${b.m}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="rounded-sm border border-border bg-card overflow-hidden" data-testid="leaves-report-card-by-agent">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border bg-secondary/40">
          <h3 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">By Agent</h3>
          <span className="ml-auto text-[11px] text-muted-foreground">
            {(data?.by_user || []).length} agent{(data?.by_user || []).length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Agent Name</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Leave Type</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Total Leave Taken</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Total Approved</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Total Unapproved</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Total HD</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_user || []).length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted-foreground italic py-8">No leave activity this year yet.</td></tr>
              )}
              {(data?.by_user || []).map((row) => (
                <tr key={row.user_id} className="border-t border-border first:border-0 hover:bg-secondary/30" data-testid={`leaves-report-agent-row-${row.user_id}`}>
                  <td className="px-3 py-1.5">
                    <div className="font-medium">{row.agent_name}</div>
                    <div className="text-[10px] text-muted-foreground">{row.agent_email}</div>
                  </td>
                  <td className="px-3 py-1.5 font-mono">{row.leave_types || "—"}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{row.total_days}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-emerald-700">{row.approved}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-rose-700">{row.unapproved}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-violet-700">{row.hd_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * Shift Change — agent applies; admin approves/rejects (auto-applies
 * the requested role schedule between effective_date and end_date).
 * Visible to ALL roles. Admin gets an extra "Pending Requests" sub-panel
 * to decide on incoming applications.
 * ───────────────────────────────────────────────────────────────────────── */
function ShiftChangePanel({ isAdmin }) {
  const today = new Date().toISOString().slice(0, 10);
  const [schedules, setSchedules] = useState([]);
  const [standardShifts, setStandardShifts] = useState([]);
  const [mine, setMine] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busy, setBusy] = useState(false);

  // Form state stores the dropdown's full variant `key` (role|start|end|tz)
  // so picking the same role with different timings disambiguates.
  // Dates are EMPTY by default so agents must explicitly pick — prevents
  // accidental same-day submissions when the user forgot to set them.
  const [form, setForm] = useState({
    current_shift_key: "",
    requested_shift_key: "",
    effective_date: "",
    end_date: "",
    reason: "",
  });
  const setF = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: typeof e === "object" ? e.target.value : e }));

  // Synthetic key → schedule lookup. The backend returns each variant
  // with a unique `key` field; we keep that as the dropdown value.
  // We index BOTH the Current Shift catalog and the Requested Shift
  // standard list so submit() can resolve either side.
  const scheduleByKey = useMemo(() => {
    const m = {};
    schedules.forEach((s) => { m[s.key] = s; });
    standardShifts.forEach((s) => { m[s.key] = s; });
    return m;
  }, [schedules, standardShifts]);

  async function fetchAll() {
    try {
      const [s, m, std] = await Promise.all([
        api.get("/leaves/shift-change/schedules"),
        api.get("/leaves/shift-change/mine"),
        api.get("/leaves/shift-change/standard-shifts"),
      ]);
      setSchedules(s.data.schedules || []);
      setMine(m.data.requests || []);
      setStandardShifts(std.data.shifts || []);
      // Auto-preselect the user's OWN current shift on first load.
      // We DON'T overwrite if the agent already touched the dropdown —
      // a non-empty `current_shift_key` is the signal.
      const myKey = s.data.my_current_shift_key;
      if (myKey) {
        setForm((prev) => prev.current_shift_key ? prev : { ...prev, current_shift_key: myKey });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }

  async function fetchAdminQueue(stat) {
    if (!isAdmin) return;
    try {
      const r = await api.get(`/leaves/shift-change/requests?status=${stat}`);
      setAdmins(r.data.requests || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { fetchAdminQueue(statusFilter); }, [statusFilter, isAdmin]);

  async function submit() {
    if (!form.current_shift_key) return toast.error("Pick your current shift.");
    if (!form.requested_shift_key) return toast.error("Pick a requested shift.");
    if (!form.effective_date) return toast.error("Select an effective date.");
    if (!form.end_date) return toast.error("Select an end date.");
    if (!form.reason.trim()) return toast.error("A reason is required.");
    if (form.end_date < form.effective_date) return toast.error("End date must be on or after effective date.");
    const requested = scheduleByKey[form.requested_shift_key];
    const current = scheduleByKey[form.current_shift_key] || null;
    if (!requested) return toast.error("Selected shift is no longer available — refresh.");
    setBusy(true);
    try {
      await api.post("/leaves/shift-change/apply", {
        current_shift_role: current?.role_name || "",
        requested_shift_role: requested.role_name,
        requested_shift_snapshot: {
          start_time: requested.start_time,
          end_time: requested.end_time,
          timezone: requested.timezone || null,
          days_of_week: requested.days_of_week || [],
          break_windows: requested.break_windows || [],
        },
        effective_date: form.effective_date,
        end_date: form.end_date,
        reason: form.reason,
      });
      toast.success("Shift change request submitted.");
      setForm({ current_shift_key: form.current_shift_key, requested_shift_key: "", effective_date: "", end_date: "", reason: "" });
      fetchAll();
      fetchAdminQueue(statusFilter);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id) {
    if (!window.confirm("Cancel this pending request?")) return;
    try {
      await api.delete(`/leaves/shift-change/${id}`);
      toast.success("Request cancelled.");
      fetchAll();
      fetchAdminQueue(statusFilter);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cancel failed");
    }
  }

  async function decide(id, action) {
    const labels = { approve: "Approve", reject: "Reject", revoke: "Revoke" };
    const label = labels[action] || action;
    if (!window.confirm(`${label} this shift change request?`)) return;
    const reason = (action === "reject" || action === "revoke")
      ? (window.prompt("Reason (optional):") || "")
      : "";
    try {
      await api.post(`/leaves/shift-change/${id}/${action}`, { reason });
      toast.success(`${label}d`);
      fetchAdminQueue(statusFilter);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `${label} failed`);
    }
  }

  async function adminDelete(id) {
    if (!window.confirm("Permanently delete this shift change request? If it was approved, the agent will be reverted to their original shift.")) return;
    try {
      await api.delete(`/leaves/shift-change/${id}`);
      toast.success("Deleted");
      fetchAdminQueue(statusFilter);
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }

  // Helper to render a per-row snapshot for "My Requests" / Admin tables —
  // each row stores `requested_shift_snapshot` when the user picked a
  // multi-variant role. Falls back to the role-default fmt when missing.
  function fmtRowShift(row, key /* current|requested */) {
    if (key === "requested") {
      const snap = row.requested_shift_snapshot || {};
      if (snap.start_time && snap.end_time) {
        return `${row.requested_shift_role} (${snap.start_time}–${snap.end_time}${snap.timezone ? " " + snap.timezone : ""})`;
      }
      return row.requested_shift_role || "—";
    }
    return row.current_shift_role || "—";
  }


  return (
    <div className="space-y-6 pt-4" data-testid="shift-change-panel">
      <div className="border border-border bg-card rounded-sm p-4">
        <div className="text-sm font-semibold mb-3">Request a Shift Change</div>
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          <label className="space-y-1">
            <div className="text-muted-foreground">Current Shift</div>
            <select
              className="form-input w-full text-xs"
              value={form.current_shift_key}
              onChange={setF("current_shift_key")}
              data-testid="shift-change-current"
            >
              <option value="">— Select your current shift —</option>
              {schedules.map((s) => (
                <option key={s.key} value={s.key}>
                  {`${s.role_name} (${s.start_time}–${s.end_time}${s.timezone ? " " + s.timezone : ""}${s.enabled === false ? " · inactive" : ""})`}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <div className="text-muted-foreground">Requested Shift</div>
            <select
              className="form-input w-full text-xs"
              value={form.requested_shift_key}
              onChange={setF("requested_shift_key")}
              data-testid="shift-change-requested"
            >
              <option value="">— Select requested shift —</option>
              {standardShifts.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <div className="text-muted-foreground">Effective Date</div>
            <Input type="date" value={form.effective_date} onChange={setF("effective_date")} min={today} data-testid="shift-change-effective-date" />
          </label>
          <label className="space-y-1">
            <div className="text-muted-foreground">End Date</div>
            <Input type="date" value={form.end_date} onChange={setF("end_date")} min={form.effective_date || today} data-testid="shift-change-end-date" />
          </label>
          <label className="space-y-1 md:col-span-2">
            <div className="text-muted-foreground">Reason</div>
            <textarea
              rows={2}
              className="form-input w-full text-xs"
              placeholder="e.g. Medical appointment — need EU shift for the week"
              value={form.reason}
              onChange={setF("reason")}
              data-testid="shift-change-reason"
            />
          </label>
        </div>
        <div className="mt-3 flex justify-end">
          <Button size="sm" className="rounded-sm h-8 text-xs" disabled={busy} onClick={submit} data-testid="shift-change-submit">
            {busy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Plus size={12} className="mr-1" />}
            Submit Request
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm">
        <div className="px-4 py-2 text-sm font-semibold border-b border-border flex items-center justify-between">
          <span>My Shift Change Requests</span>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={fetchAll}>
            <RefreshCw size={11} className="mr-1" /> Refresh
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="shift-change-mine-table">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Current</th>
                <th className="px-3 py-2 text-left">Requested</th>
                <th className="px-3 py-2 text-left">From</th>
                <th className="px-3 py-2 text-left">To</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Decided By</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {mine.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">No requests yet.</td></tr>
              ) : mine.map((row) => (
                <tr key={row.id} className="border-t border-border" data-testid={`shift-change-row-${row.id}`}>
                  <td className="px-3 py-1.5">{fmtRowShift(row, "current")}</td>
                  <td className="px-3 py-1.5 font-medium">{fmtRowShift(row, "requested")}</td>
                  <td className="px-3 py-1.5 font-mono">{row.effective_date}</td>
                  <td className="px-3 py-1.5 font-mono">{row.end_date}</td>
                  <td className="px-3 py-1.5 max-w-xs truncate" title={row.reason}>{row.reason}</td>
                  <td className="px-3 py-1.5">
                    <Badge className={`text-[10px] rounded-sm ${STATUS_TONE[row.status] || ""}`}>{row.status}</Badge>
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">{row.decided_by || "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    {row.status === "pending" && (
                      <Button size="sm" variant="ghost" className="h-6 text-[11px] text-rose-700 hover:bg-rose-50" onClick={() => cancel(row.id)}>
                        <Trash2 size={10} className="mr-1" /> Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isAdmin && (
        <div className="border border-border bg-card rounded-sm">
          <div className="px-4 py-2 text-sm font-semibold border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <span>Shift Change Requests (Admin)</span>
            <div className="flex gap-1 ml-auto">
              {["pending", "approved", "revoked", "rejected"].map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={statusFilter === s ? "default" : "outline"}
                  className="rounded-sm h-7 text-[11px] capitalize"
                  onClick={() => setStatusFilter(s)}
                  data-testid={`shift-change-admin-filter-${s}`}
                >{s}</Button>
              ))}
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => fetchAdminQueue(statusFilter)}>
                <RefreshCw size={11} className="mr-1" /> Refresh
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="shift-change-admin-table">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Employee</th>
                  <th className="px-3 py-2 text-left">Current</th>
                  <th className="px-3 py-2 text-left">Requested</th>
                  <th className="px-3 py-2 text-left">From</th>
                  <th className="px-3 py-2 text-left">To</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {admins.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No {statusFilter} requests.</td></tr>
                ) : admins.map((row) => (
                  <tr key={row.id} className="border-t border-border" data-testid={`shift-change-admin-row-${row.id}`}>
                    <td className="px-3 py-1.5">
                      <div className="font-medium">{row.user_name}</div>
                      <div className="text-[10px] text-muted-foreground">{row.user_email}</div>
                    </td>
                    <td className="px-3 py-1.5">{fmtRowShift(row, "current")}</td>
                    <td className="px-3 py-1.5 font-medium">{fmtRowShift(row, "requested")}</td>
                    <td className="px-3 py-1.5 font-mono">{row.effective_date}</td>
                    <td className="px-3 py-1.5 font-mono">{row.end_date}</td>
                    <td className="px-3 py-1.5 max-w-xs truncate" title={row.reason}>{row.reason}</td>
                    <td className="px-3 py-1.5 text-right">
                      {row.status === "pending" ? (
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-sm h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => decide(row.id, "approve")}
                            data-testid={`shift-change-approve-${row.id}`}
                          ><Check size={11} className="mr-1" /> Approve</Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-sm h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                            onClick={() => decide(row.id, "reject")}
                            data-testid={`shift-change-reject-${row.id}`}
                          ><X size={11} className="mr-1" /> Reject</Button>
                        </div>
                      ) : row.status === "approved" ? (
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-sm h-7 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => decide(row.id, "revoke")}
                            data-testid={`shift-change-revoke-${row.id}`}
                            title="Cancel this approved shift change and revert the agent to their original schedule"
                          ><RefreshCw size={11} className="mr-1" /> Revoke</Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-sm h-7 text-[11px] text-rose-700 hover:bg-rose-50"
                            onClick={() => adminDelete(row.id)}
                            data-testid={`shift-change-delete-${row.id}`}
                          ><Trash2 size={11} className="mr-1" /> Delete</Button>
                        </div>
                      ) : (
                        <div className="inline-flex gap-2 items-center">
                          <span className="text-[11px] text-muted-foreground">{row.revoked_by || row.decided_by || "—"}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-sm h-7 text-[11px] text-rose-700 hover:bg-rose-50"
                            onClick={() => adminDelete(row.id)}
                            data-testid={`shift-change-delete-${row.id}`}
                          ><Trash2 size={11} className="mr-1" /> Delete</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

